import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { AppRoute } from "../routes/AppRoutes";
import ModalSlice from "./slices/modalSlice";
import NavigationSlice from "./slices/navigationSlice";
import UiSlice from "./slices/uiSlice";

const STORAGE_KEY = "redux_state";

const rootReducer = combineReducers({
  modal: ModalSlice,
  navigation: NavigationSlice,
  ui: UiSlice,
});

const isObject = (item: any) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

const deepMerge = (target: any, source: any): any => {
  let output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

const loadState = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);

    if (!serializedState) return undefined;

    const persisted = JSON.parse(serializedState);

    const defaultState = rootReducer(undefined, { type: "@@INIT" });
    const merged = deepMerge(defaultState, persisted);

    // a route persisted by an older build may have been removed since
    if (!Object.values(AppRoute).includes(merged.navigation?.currentRoute)) {
      merged.navigation = { ...merged.navigation, currentRoute: defaultState.navigation.currentRoute };
    }

    return merged;
  } catch (err) {
    console.error("Hydration failed", err);
    return undefined;
  }
};

const saveState = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (err) {
    console.error("Could not save state", err);
  }
};

const store = configureStore({
  reducer: rootReducer,
  preloadedState: loadState()
})


const debounce = (fn: Function, ms: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

const debouncedSave = debounce(() => {
  saveState(store.getState());
}, 250);

store.subscribe(debouncedSave);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;